import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { PortalPage } from "@/components/portal/PortalPage";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { listMyAnalyses, createAnalysis, deleteAnalysis } from "@/lib/una.functions";
import { HOUSEHOLD_LABEL } from "@/lib/una.summary";
import { Trash2 } from "lucide-react";

export const Route = createFileRoute("/agent/una")({
  head: () => ({
    meta: [
      { title: "Needs Analysis — Strategist Portal" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <UNAList />
      </PortalShell>
    </ProtectedRoute>
  ),
});

type Row = {
  id: string;
  client_name: string;
  client_id: string | null;
  status: "draft" | "completed";
  progress_pct: number;
  household_type: string | null;
  monthly_capacity: number | null;
  completed_at: string | null;
  updated_at: string;
};

function fmtCurrency(n: number | null) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function UNAList() {
  const list = useServerFn(listMyAnalyses);
  const create = useServerFn(createAnalysis);
  const del = useServerFn(deleteAnalysis);
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const r = await list();
      setRows((r.rows ?? []) as Row[]);
    } finally { setLoading(false); }
  }

  useEffect(() => { void reload(); }, []);

  async function handleCreate() {
    if (!name.trim()) { toast.error("Client name is required"); return; }
    setBusy(true);
    try {
      const r = await create({ data: { clientName: name.trim() } });
      setOpen(false); setName("");
      navigate({ to: "/agent/una/$id", params: { id: r.id } });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not start analysis");
    } finally { setBusy(false); }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this analysis? This cannot be undone.")) return;
    try {
      await del({ data: { id } });
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (e: any) { toast.error(e?.message ?? "Could not delete"); }
  }

  return (
    <PortalPage
      eyebrow="Needs Analysis"
      title="Universal Needs Analysis"
      intro="Walk a household from discovery to action plan in one guided framework. Every analysis is saved, resumable, and ready to export."
      actions={<PrimaryButton onClick={() => setOpen(true)}>+ Start New Analysis</PrimaryButton>}
    >
      <div className="bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center font-sans text-[13px] text-charcoal-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="font-display text-[20px] text-navy-800">No analyses yet</p>
            <p className="mt-2 font-sans text-[13px] text-charcoal-500">
              Start an analysis to capture priorities, risks, and an action plan in one place.
            </p>
            <div className="mt-6 inline-block">
              <PrimaryButton onClick={() => setOpen(true)}>+ Start New Analysis</PrimaryButton>
            </div>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead className="bg-cream-50 border-b border-bronze-600/20">
              <tr className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">
                <th className="px-5 py-3">Client</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Progress</th>
                <th className="px-5 py-3">Household</th>
                <th className="px-5 py-3">Capacity / mo</th>
                <th className="px-5 py-3">Updated</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-bronze-600/10 hover:bg-cream-50/50">
                  <td className="px-5 py-4">
                    <Link to="/agent/una/$id" params={{ id: r.id }} className="font-display text-[15px] text-navy-800 hover:text-bronze-600">
                      {r.client_name}
                    </Link>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-0.5 rounded-full font-sans text-[10px] uppercase tracking-[0.12em] ${
                      r.status === "completed"
                        ? "bg-bronze-600 text-cream-50"
                        : "bg-bronze-600/15 text-bronze-600"
                    }`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 font-sans text-[13px] text-charcoal-900">{r.progress_pct}%</td>
                  <td className="px-5 py-4 font-sans text-[13px] text-charcoal-900">
                    {r.household_type ? HOUSEHOLD_LABEL[r.household_type as keyof typeof HOUSEHOLD_LABEL] ?? "—" : "—"}
                  </td>
                  <td className="px-5 py-4 font-display text-[14px] text-navy-800">{fmtCurrency(r.monthly_capacity)}</td>
                  <td className="px-5 py-4 font-sans text-[12px] text-charcoal-500">
                    {new Date(r.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => handleDelete(r.id)} className="text-charcoal-500 hover:text-danger" aria-label="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-navy-800">Start Needs Analysis</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="una-name">Client / Prospect Name</Label>
              <Input id="una-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. The Johnson Family" />
            </div>
            <p className="font-sans text-[11px] text-charcoal-500">
              You can link this to an existing client later. The analysis is private to you until you mark it complete.
            </p>
          </div>
          <DialogFooter>
            <PrimaryButton onClick={handleCreate} disabled={busy}>{busy ? "Creating…" : "Begin Analysis"}</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalPage>
  );
}